// src/contexts/CardCreationContext.tsx
import React, { createContext, useContext, useState, useMemo } from "react";
import { StudioContextType, StepId, TabId } from "../types/studio.types";
import { CardCustomization, CardData } from "../types/card.types";
import { calculateCompletionPercentage } from "../utils/progressUtils";
import { useCustomization } from "../hooks/useCustomization";

const StudioContext = createContext<StudioContextType | undefined>(undefined);

export const useStudio = () => {
  const context = useContext(StudioContext);
  if (!context) throw new Error("useStudio must be used within StudioProvider");
  return context;
};

interface StudioProviderProps {
  children: React.ReactNode;
  template: any;
  user: any;
  customization: CardCustomization | null;
  setCustomization: React.Dispatch<
    React.SetStateAction<CardCustomization | null>
  >;
  createdCard?: CardData | null;
}

export const StudioProvider: React.FC<StudioProviderProps> = ({
  children,
  template,
  user,
  customization,
  setCustomization,
  createdCard = null,
}) => {
  const [activeStep, setActiveStep] = useState<StepId>(1);
  const [activeTab, setActiveTab] = useState<TabId>("style");
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const { updateCustomization } = useCustomization(setCustomization);

  const completionPercentage = useMemo(
    () => calculateCompletionPercentage(customization),
    [customization],
  );

  const value = useMemo(
    () => ({
      customization,
      setCustomization,
      updateCustomization,
      completionPercentage,
      activeStep,
      setActiveStep,
      activeTab,
      setActiveTab,
      isPreviewMode,
      setIsPreviewMode,
      createdCard: createdCard || null,
      hasUnsavedChanges,
      setHasUnsavedChanges,
    }),
    [
      customization,
      setCustomization,
      updateCustomization,
      completionPercentage,
      activeStep,
      activeTab,
      isPreviewMode,
      createdCard,
      hasUnsavedChanges,
    ],
  );

  return (
    <StudioContext.Provider value={value}>{children}</StudioContext.Provider>
  );
};
